import clsx from 'clsx';

// Types/Constants
import type { SpGroupType } from '@/hooks/swr/group/useSPGroups';
import useTranslation from '@/utils/hooks/translation/useTranslation';

// Utils
import { getAnalyticsGroupUsedNetworkTypes } from '@/utils/analytics/getAnalyticsGroupUsedNetworkTypes';

import type { SocialProfileNetworkType } from '@/types/socialProfileType';

// Lib
import {
    FACEBOOK,
    INSTAGRAM,
    LINKEDIN,
    PINTEREST,
    TIKTOK_BUSINESS,
    X,
    YOUTUBE
} from '@/lib/socialProfile';

// Components
import Icon from '@/components/base/Icon';
import Instagram from '@/components/icons/Instagram';
import { PinterestIcon } from '@/components/icons/Networks/PinterestIcon';
import { YoutubeIcon } from '@/components/icons/Networks/YoutubeIcon';

type AggregatedNetworksListProps = {
    group: SpGroupType;
    aggregatedNetworkTypes: SocialProfileNetworkType[];
    forceLightMode?: boolean;
    className?: string;
};

const NetworkIcon = ({
    networkType,
    forceLightMode
}: {
    networkType: SocialProfileNetworkType;
    forceLightMode: boolean;
}) => {
    switch (networkType) {
        case INSTAGRAM:
            return <Instagram key={networkType} />;
        case PINTEREST:
            return <PinterestIcon key={networkType} />;
        case YOUTUBE:
            return <YoutubeIcon key={networkType} />;
        default:
            return (
                <Icon
                    data-testid={`logo-${networkType}`}
                    key={networkType}
                    icon={networkType ?? ''}
                    className={clsx('text-xl', {
                        'text-facebook': networkType === FACEBOOK,
                        'text-linkedin': networkType === LINKEDIN,
                        'text-twitter': networkType === X,
                        'text-tiktok': networkType === TIKTOK_BUSINESS,
                        'dark:text-white': !forceLightMode
                    })}
                />
            );
    }
};

const AggregatedNetworksList = ({
    group,
    aggregatedNetworkTypes,
    forceLightMode = false,
    className
}: AggregatedNetworksListProps) => {
    const { t } = useTranslation('analytics');

    if (!group || !aggregatedNetworkTypes) {
        return null;
    }

    const usedNetworkTypes = getAnalyticsGroupUsedNetworkTypes(group, aggregatedNetworkTypes);

    if (!usedNetworkTypes?.length) {
        return null;
    }

    return (
        <div className={clsx('font-GMedium text-xxs text-icoGray-500', className)}>
            <p>{t('data_aggregated_from')}</p>
            <div className="flex items-center space-x-2">
                {[...usedNetworkTypes].sort().map((networkType: SocialProfileNetworkType) => (
                    <NetworkIcon
                        key={networkType}
                        networkType={networkType}
                        forceLightMode={forceLightMode}
                    />
                ))}
            </div>
        </div>
    );
};

export default AggregatedNetworksList;
